import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import { usePaymentFlow, BookingStatus } from "./usePaymentFlow";
import ProcessingModal from "../modal/ProcessingModal";
import SuccessModal from "../modal/SuccessModal";
import FailureModal from "../modal/FailureModal";

const TestPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [firebaseToken, setFirebaseToken] = useState(null);
  const { paymentState, initiatePayment, resetPayment } = usePaymentFlow();

  // Extract parameters from URL params
  const searchParams = new URLSearchParams(location.search);
  const rc_booking_id_from_url = searchParams.get("rc_booking_id");
  const trace_id_from_url = searchParams.get("trace_id");
  const result_idx_from_url = searchParams.get("result_idx");

  // Extract parameters from location.state
  const rc_booking_id_from_state = location.state?.rc_booking_id;
  const trace_id_from_state = location.state?.trace_id;
  const result_idx_from_state = location.state?.result_idx;
  const userToken_from_state = location.state?.userToken;
  const amount_from_state = location.state?.amount || "5000"; // Default amount for testing

  // Get final values (prefer state over URL)
  const rc_booking_id = rc_booking_id_from_state || rc_booking_id_from_url;
  const trace_id = trace_id_from_state || trace_id_from_url;
  const result_idx = result_idx_from_state || result_idx_from_url;

  // Get user token from Firebase SDK
  useEffect(() => {
    const getFirebaseToken = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          setFirebaseToken(token);
        } else {
          console.error("No authenticated user found");
        }
      } catch (error) {
        console.error("Error getting Firebase token:", error);
      }
    };

    getFirebaseToken();

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setFirebaseToken(token);
        } catch (error) {
          console.error("Error getting Firebase token:", error);
        }
      } else {
        setFirebaseToken(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Determine which token to use (preference: Firebase SDK > location.state)
  const effectiveUserToken = firebaseToken || userToken_from_state;

  // Handle payment initiation
  const handleProceedToPayment = async () => {
    if (!rc_booking_id || !trace_id || !result_idx || !effectiveUserToken) {
      alert("Missing required parameters. Please check the URL or navigation state.");
      return;
    }

    await initiatePayment(
      rc_booking_id,
      trace_id,
      result_idx,
      amount_from_state,
      effectiveUserToken
    );
  };

  // Handle retry
  const handleRetry = () => {
    resetPayment();
  };

  // Handle contact support
  const handleContactSupport = () => {
    // Navigate to support page or open email client
    window.location.href = "mailto:support@routechef.com";
  };

  // Render appropriate modal based on payment state
  const renderModal = () => {
    const { status, isProcessing, statusData } = paymentState;

    // Processing states
    if (isProcessing || status === "CREATING_ORDER") {
      return <ProcessingModal title="Processing Your Payment" />;
    }

    if (status === BookingStatus.PAYMENT_PENDING) {
      return <ProcessingModal title="Processing" message="This will only take a moment. Thank you for your patience." />;
    }

    if (status === BookingStatus.BOOKING_IN_PROGRESS) {
      return <ProcessingModal title="Processing" message="Your booking is being processed. This will only take a moment." />;
    }

    // Success state - payment success but ticket pending
    if (status === BookingStatus.PAYMENT_SUCCESS) {
      return <SuccessModal ticketDetails={statusData} />;
    }

    // Final success state - ticket issued
    if (status === BookingStatus.TICKET_ISSUED) {
      return (
        <SuccessModal
          ticketDetails={statusData}
          onClose={() => {
            // Navigate to booking details or tickets page
            // console.log("Navigate to booking details", statusData);
          }}
        />
      );
    }

    // Failure states
    if (status === BookingStatus.PAYMENT_FAILED || status === BookingStatus.TICKET_FAILED) {
      return (
        <FailureModal
          onRetry={handleRetry}
          onContactSupport={handleContactSupport}
          errorMessage={paymentState.error}
        />
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Render modals */}
      {renderModal()}

      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold mb-6 text-gray-800">Test Payment Page</h1>

        <div className="space-y-6">
          {/* RC Booking ID Section */}
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-3 text-gray-700">RC Booking ID</h2>
            <div className="space-y-2 ml-4">
              <p className="text-gray-600">
                <strong>From URL:</strong>
                <span className="ml-2 font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                  {rc_booking_id_from_url || 'Not found'}
                </span>
              </p>
              <p className="text-gray-600">
                <strong>From State:</strong>
                <span className="ml-2 font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                  {rc_booking_id_from_state || 'Not found'}
                </span>
              </p>
            </div>
          </div>

          {/* Trace ID Section */}
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-3 text-gray-700">Trace ID</h2>
            <div className="space-y-2 ml-4">
              <p className="text-gray-600">
                <strong>From URL:</strong>
                <span className="ml-2 font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                  {trace_id_from_url || 'Not found'}
                </span>
              </p>
              <p className="text-gray-600">
                <strong>From State:</strong>
                <span className="ml-2 font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                  {trace_id_from_state || 'Not found'}
                </span>
              </p>
            </div>
          </div>

          {/* Result Index Section */}
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-3 text-gray-700">Result Index</h2>
            <div className="space-y-2 ml-4">
              <p className="text-gray-600">
                <strong>From URL:</strong>
                <span className="ml-2 font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                  {result_idx_from_url || 'Not found'}
                </span>
              </p>
              <p className="text-gray-600">
                <strong>From State:</strong>
                <span className="ml-2 font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                  {result_idx_from_state || 'Not found'}
                </span>
              </p>
            </div>
          </div>

          {/* User Token Section */}
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-3 text-gray-700">User Token</h2>
            <div className="space-y-2 ml-4">
              <p className="text-gray-600">
                <strong>From Firebase SDK:</strong>
                <span className="ml-2 font-mono text-xs bg-blue-100 px-2 py-1 rounded break-all">
                  {firebaseToken ? `${firebaseToken.substring(0, 50)}...` : 'Not available'}
                </span>
              </p>
              <p className="text-gray-600">
                <strong>From State:</strong>
                <span className="ml-2 font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">
                  {userToken_from_state ? `${userToken_from_state.substring(0, 50)}...` : 'Not found'}
                </span>
              </p>
              <p className="text-gray-600">
                <strong>Effective Token:</strong>
                <span className="ml-2 font-mono text-xs bg-green-100 px-2 py-1 rounded break-all">
                  {effectiveUserToken ? `${effectiveUserToken.substring(0, 50)}...` : 'Not available'}
                </span>
              </p>
            </div>
          </div>

          {/* Amount Section */}
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-3 text-gray-700">Amount</h2>
            <div className="space-y-2 ml-4">
              <p className="text-gray-600">
                <strong>Test Amount:</strong>
                <span className="ml-2 font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                  ₹{amount_from_state}
                </span>
              </p>
            </div>
          </div>

          {/* Summary Section */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h2 className="text-xl font-semibold mb-3 text-blue-800">Summary</h2>
            <div className="space-y-1 text-sm text-blue-700">
              <p><strong>RC Booking ID:</strong> {rc_booking_id || 'N/A'}</p>
              <p><strong>Trace ID:</strong> {trace_id || 'N/A'}</p>
              <p><strong>Result Index:</strong> {result_idx || 'N/A'}</p>
              <p><strong>Amount:</strong> ₹{amount_from_state}</p>
              <p><strong>Token Source:</strong> {firebaseToken ? 'Firebase SDK' : userToken_from_state ? 'Location State' : 'None'}</p>
            </div>
          </div>

          {/* Payment Status Section */}
          {paymentState.status && (
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h2 className="text-xl font-semibold mb-3 text-purple-800">Payment Status</h2>
              <div className="space-y-2 text-sm text-purple-700">
                <p><strong>Current Status:</strong> {paymentState.status}</p>
                <p><strong>Is Processing:</strong> {paymentState.isProcessing ? 'Yes' : 'No'}</p>
                {paymentState.error && (
                  <p className="text-red-600"><strong>Error:</strong> {paymentState.error}</p>
                )}
                {paymentState.orderData && (
                  <details className="mt-2">
                    <summary className="cursor-pointer font-semibold">Order Data</summary>
                    <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto max-h-40">
                      {JSON.stringify(paymentState.orderData, null, 2)}
                    </pre>
                  </details>
                )}
                {paymentState.statusData && (
                  <details className="mt-2">
                    <summary className="cursor-pointer font-semibold">Status Data</summary>
                    <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto max-h-40">
                      {JSON.stringify(paymentState.statusData, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleProceedToPayment}
              disabled={!rc_booking_id || !trace_id || !result_idx || !effectiveUserToken || paymentState.isProcessing}
              className={`flex-1 py-3 px-6 rounded-lg font-semibold text-white transition-colors ${!rc_booking_id || !trace_id || !result_idx || !effectiveUserToken || paymentState.isProcessing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
                }`}
            >
              {paymentState.isProcessing ? 'Processing...' : 'Proceed to Payment'}
            </button>
            {paymentState.status && (
              <button
                onClick={resetPayment}
                disabled={paymentState.isProcessing}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPayment;
